import { useEffect, useMemo, useRef, useState } from 'react';
import api from '../../api/client';
import { useAuth } from '../../context/AuthContext';

export default function Actividades() {
  const { usuario } = useAuth();

  const [items, setItems]               = useState([]);
  const [tipos, setTipos]               = useState([]);
  const [diagnos, setDiagnos]           = useState([]);
  const [tecnologias, setTecnologias]   = useState([]);
  const [responsables, setResponsables] = useState([]);
  const [sectores, setSectores]         = useState([]);
  const [loading, setLoading]           = useState(true);
  const [modal, setModal]               = useState(null);

  const [filtros, setFiltros] = useState({
    desde: '', hasta: '', tipo_alerta_id: '', responsable_id: '',
  });

  const cargar = async () => {
    setLoading(true);
    try {
      const params = Object.fromEntries(
        Object.entries(filtros).filter(([, v]) => v !== '')
      );
      // Finca activa: localStorage (super_admin) → usuario.finca_id (fallback)
      const fincaId =
        localStorage.getItem('fincaActivaId') ||
        usuario?.finca_id ||
        '';

      const [a, t, d, tec, resp, sec] = await Promise.all([
        api.get('/alimentacion/actividades', { params }),
        api.get('/alimentacion/tipos-alerta'),
        api.get('/alimentacion/diagnosticos'),
        api.get('/alimentacion/tecnologias'),
        api.get('/alimentacion/responsables'),
        fincaId ? api.get(`/sectores/finca/${fincaId}`) : Promise.resolve({ data: [] }),
      ]);
      setItems(a.data);
      setTipos(t.data);
      setDiagnos(d.data);
      setTecnologias(tec.data);
      setResponsables(resp.data);
      setSectores(Array.isArray(sec.data) ? sec.data : []);
    } catch (err) {
      console.error('Error cargando actividades:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { cargar(); /* eslint-disable-next-line */ }, []);

  const guardar = async (form) => {
    try {
      if (form.id) await api.put(`/alimentacion/actividades/${form.id}`, form);
      else         await api.post('/alimentacion/actividades', form);
      setModal(null);
      cargar();
    } catch (e) {
      alert(e.response?.data?.error || 'Error al guardar');
    }
  };

  const eliminar = async (id) => {
    if (!confirm('¿Eliminar esta actividad?')) return;
    await api.delete(`/alimentacion/actividades/${id}`);
    cargar();
  };

  const formatearTiempo = (seg) => {
    if (seg == null) return '—';
    const s = Math.abs(seg);
    if (s < 60)   return `${s} seg`;
    if (s < 3600) return `${Math.floor(s/60)} min${s%60 ? ' ' + (s%60) + ' seg' : ''}`;
    return `${Math.floor(s/3600)} h${Math.floor((s%3600)/60) ? ' ' + Math.floor((s%3600)/60) + ' min' : ''}`;
  };

  const colorEstado = {
    abierta:    'bg-red-100 text-red-700',
    en_proceso: 'bg-amber-100 text-amber-700',
    resuelta:   'bg-green-100 text-green-700',
  };

  const etiquetaEstado = {
    abierta:    'Abierta',
    en_proceso: 'En proceso',
    resuelta:   'Resuelta',
  };

  return (
    <div className="p-6 space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <h1 className="text-2xl font-bold">Registrar Actividad</h1>
        <button
          onClick={() => setModal({})}
          className="ml-auto bg-sky-600 hover:bg-sky-700 text-white px-4 py-2 rounded"
        >
          + Nueva actividad
        </button>
      </div>

      {/* Filtros */}
      <div className="bg-white rounded-xl shadow p-4 flex flex-wrap gap-3 items-end">
        <div>
          <label className="block text-xs text-gray-500">Desde</label>
          <input type="date" value={filtros.desde}
                 onChange={e => setFiltros(f => ({ ...f, desde: e.target.value }))}
                 className="border rounded px-3 py-2" />
        </div>
        <div>
          <label className="block text-xs text-gray-500">Hasta</label>
          <input type="date" value={filtros.hasta}
                 onChange={e => setFiltros(f => ({ ...f, hasta: e.target.value }))}
                 className="border rounded px-3 py-2" />
        </div>
        <div>
          <label className="block text-xs text-gray-500">Tipo alerta</label>
          <select value={filtros.tipo_alerta_id}
                  onChange={e => setFiltros(f => ({ ...f, tipo_alerta_id: e.target.value }))}
                  className="border rounded px-3 py-2">
            <option value="">Todas</option>
            {tipos.map(t => <option key={t.id} value={t.id}>{t.nombre}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-xs text-gray-500">Responsable</label>
          <select value={filtros.responsable_id}
                  onChange={e => setFiltros(f => ({ ...f, responsable_id: e.target.value }))}
                  className="border rounded px-3 py-2">
            <option value="">Todos</option>
            {responsables.map(r => <option key={r.id} value={r.id}>{r.nombre}</option>)}
          </select>
        </div>
        <button onClick={cargar}
                className="bg-gray-800 text-white px-4 py-2 rounded">Filtrar</button>
      </div>

      {/* Tabla */}
      <div className="bg-white rounded-xl shadow overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead className="bg-gray-50 text-gray-500 text-left">
            <tr>
              <th className="px-4 py-3">Fecha</th>
              <th className="px-4 py-3">Sector</th>
              <th className="px-4 py-3">Responsable</th>
              <th className="px-4 py-3">Hora rep.</th>
              <th className="px-4 py-3">Hora alerta</th>
              <th className="px-4 py-3">Tiempo</th>
              <th className="px-4 py-3">Piscina</th>
              <th className="px-4 py-3">Tolva</th>
              <th className="px-4 py-3">Tecnología</th>
              <th className="px-4 py-3">Alerta</th>
              <th className="px-4 py-3">Diagnóstico</th>
              <th className="px-4 py-3">Estado</th>
              <th className="px-4 py-3 w-32">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan="13" className="px-4 py-6 text-center text-gray-400">Cargando…</td></tr>
            )}
            {!loading && items.length === 0 && (
              <tr><td colSpan="13" className="px-4 py-6 text-center text-gray-400">Sin actividades</td></tr>
            )}
            {!loading && items.map(a => (
              <tr key={a.id} className="border-t hover:bg-gray-50">
                <td className="px-4 py-2 whitespace-nowrap">{a.fecha}</td>
                <td className="px-4 py-2">{a.sector_nombre || '—'}</td>
                <td className="px-4 py-2">{a.responsable_nombre || '—'}</td>
                <td className="px-4 py-2">{a.hora_reportada?.slice(0,5)}</td>
                <td className="px-4 py-2">{a.hora_alerta?.slice(0,5)}</td>
                <td className="px-4 py-2">
                  <span className="px-2 py-1 rounded-full text-xs bg-blue-100 text-blue-800">
                    {formatearTiempo(a.tiempo_segundos)}
                  </span>
                </td>
                <td className="px-4 py-2">{a.nro_piscina || '—'}</td>
                <td className="px-4 py-2">{a.nro_tolva || '—'}</td>
                <td className="px-4 py-2">{a.tecnologia_nombre || '—'}</td>
                <td className="px-4 py-2">{a.alerta_nombre}</td>
                <td className="px-4 py-2">{a.diagnostico_nombre || '—'}</td>
                <td className="px-4 py-2">
                  <span className={`px-2 py-1 rounded-full text-xs ${colorEstado[a.estado] || ''}`}>
                    {etiquetaEstado[a.estado] || a.estado}
                  </span>
                </td>
                <td className="px-4 py-2 text-right whitespace-nowrap">
                  <button onClick={() => setModal(a)}
                          className="text-sky-600 hover:underline mr-3">Editar</button>
                  <button onClick={() => eliminar(a.id)}
                          className="text-red-600 hover:underline">Eliminar</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {modal && (
        <ModalActividad
          item={modal}
          tipos={tipos}
          diagnosticos={diagnos}
          tecnologias={tecnologias}
          responsables={responsables}
          sectores={sectores}
          onCancel={() => setModal(null)}
          onSave={guardar}
        />
      )}
    </div>
  );
}

/* =========================================================
   ComboBox con búsqueda
   ========================================================= */
function ComboBox({ value, onChange, options, placeholder, getLabel, getValue }) {
  const [query, setQuery] = useState('');
  const [open, setOpen]   = useState(false);
  const ref               = useRef(null);

  const selected = options.find(o => getValue(o) === value);
  const display  = selected ? getLabel(selected) : query;

  const filtrados = useMemo(() => {
    const q = (query || '').toLowerCase().trim();
    if (!q) return options.slice(0, 30);
    return options.filter(o => getLabel(o).toLowerCase().includes(q)).slice(0, 30);
  }, [options, query, getLabel]);

  useEffect(() => {
    const onClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  const elegir = (o) => {
    onChange(getValue(o));
    setQuery('');
    setOpen(false);
  };

  const limpiar = () => {
    onChange('');
    setQuery('');
    setOpen(true);
  };

  return (
    <div className="relative" ref={ref}>
      <input
        type="text"
        value={display}
        placeholder={placeholder}
        onChange={(e) => {
          setQuery(e.target.value);
          setOpen(true);
          if (value) onChange('');
        }}
        onFocus={() => setOpen(true)}
        className="w-full border rounded px-3 py-2"
      />
      {value && (
        <button type="button" onClick={limpiar}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-700">
          ✕
        </button>
      )}
      {open && filtrados.length > 0 && (
        <div className="absolute z-20 w-full bg-white border rounded mt-1 shadow-lg max-h-56 overflow-y-auto">
          {filtrados.map(o => (
            <div key={getValue(o)} onClick={() => elegir(o)}
                 className="px-3 py-2 hover:bg-sky-50 cursor-pointer text-sm">
              {getLabel(o)}
            </div>
          ))}
        </div>
      )}
      {open && filtrados.length === 0 && query && (
        <div className="absolute z-20 w-full bg-white border rounded mt-1 shadow-lg px-3 py-2 text-sm text-gray-400">
          Sin resultados
        </div>
      )}
    </div>
  );
}

/* =========================================================
   Modal de actividad
   ========================================================= */
function ModalActividad({
  item, tipos, diagnosticos, tecnologias, responsables, sectores,
  onCancel, onSave,
}) {
  const [form, setForm] = useState({
    id: item.id || null,
    fecha: item.fecha || new Date().toISOString().slice(0, 10),
    sector_id: item.sector_id || '',
    responsable_id: item.responsable_id || '',
    hora_reportada: item.hora_reportada?.slice(0,5) || '',
    hora_alerta:    item.hora_alerta?.slice(0,5)    || '',
    nro_piscina:    item.nro_piscina || '',
    nro_tolva:      item.nro_tolva || '',
    tecnologia_id:  item.tecnologia_id || '',
    tipo_alerta_id: item.tipo_alerta_id || '',
    tipo_diagnostico_id: item.tipo_diagnostico_id || '',
    observaciones:  item.observaciones || '',
    estado:         item.estado === 'resuelta' ? 'resuelta' : 'en_proceso',
  });

  const [piscinasSug, setPiscinasSug] = useState([]);
  const [mostrarSug, setMostrarSug]   = useState(false);
  const [error, setError]             = useState(null);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  /* Diagnósticos filtrados por tipo de alerta (con fallback a todos) */
  const diagFiltrados = useMemo(() => {
    if (!form.tipo_alerta_id) return diagnosticos;
    const filtrados = diagnosticos.filter(
      d => d.tipo_alerta_id === form.tipo_alerta_id
    );
    return filtrados.length > 0 ? filtrados : diagnosticos;
  }, [diagnosticos, form.tipo_alerta_id]);

  /* Tiempo calculado en vivo */
  const tiempoSeg = useMemo(() => {
    if (!form.hora_reportada || !form.hora_alerta) return null;
    const [h1, m1] = form.hora_reportada.split(':').map(Number);
    const [h2, m2] = form.hora_alerta.split(':').map(Number);
    return Math.abs((h2 * 60 + m2) - (h1 * 60 + m1)) * 60;
  }, [form.hora_reportada, form.hora_alerta]);

  const buscarPiscina = async (q) => {
    if (!form.sector_id) { setPiscinasSug([]); return; }
    try {
      const params = { q: q || '', sector_id: form.sector_id };
      const r = await api.get('/alimentacion/buscar-piscinas', { params });
      setPiscinasSug(r.data);
      setMostrarSug(true);
    } catch {
      setPiscinasSug([]);
    }
  };

  const cambiarSector = (sector_id) => {
    setForm(f => ({ ...f, sector_id, nro_piscina: '' }));
    setPiscinasSug([]);
    setMostrarSug(false);
    if (sector_id) {
      api.get('/alimentacion/buscar-piscinas', { params: { q: '', sector_id } })
        .then(r => setPiscinasSug(r.data))
        .catch(() => setPiscinasSug([]));
    }
  };

  const formatearTiempo = (seg) => {
    if (seg == null) return '—';
    const s = Math.abs(seg);
    if (s < 60)   return `${s} seg`;
    if (s < 3600) return `${Math.floor(s/60)} min${s%60 ? ' ' + (s%60) + ' seg' : ''}`;
    return `${Math.floor(s/3600)} h${Math.floor((s%3600)/60) ? ' ' + Math.floor((s%3600)/60) + ' min' : ''}`;
  };

  const guardar = () => {
    setError(null);
    if (!form.fecha)          return setError('La fecha es obligatoria');
    if (!form.sector_id)      return setError('El sector es obligatorio');
    if (!form.responsable_id) return setError('El responsable es obligatorio');
    if (!form.tipo_alerta_id) return setError('El tipo de alerta es obligatorio');
    if (!form.hora_reportada) return setError('La hora reportada es obligatoria');
    if (!form.hora_alerta)    return setError('La hora alerta es obligatoria');

    onSave({
      ...form,
      sector_id: form.sector_id || null,
      tecnologia_id: form.tecnologia_id || null,
      nro_piscina: form.nro_piscina || null,
      nro_tolva: form.nro_tolva || null,
      tipo_diagnostico_id: form.tipo_diagnostico_id || null,
      observaciones: form.observaciones || null,
    });
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4 overflow-y-auto">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-3xl my-8">
        <div className="px-5 py-3 border-b font-semibold bg-sky-50 text-sky-900">
          {form.id ? 'Editar' : 'Nueva'} actividad
        </div>

        <div className="p-5 space-y-4">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 text-sm p-2 rounded">
              {error}
            </div>
          )}

          {/* Fila 1: Fecha, Sector */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                📅 Fecha <span className="text-red-500">*</span>
              </label>
              <input type="date" value={form.fecha}
                     onChange={e => set('fecha', e.target.value)}
                     className="w-full border rounded px-3 py-2" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                📍 Sector <span className="text-red-500">*</span>
              </label>
              <select value={form.sector_id}
                      onChange={e => cambiarSector(e.target.value)}
                      className="w-full border rounded px-3 py-2">
                <option value="">Selecciona</option>
                {sectores.map(s => (
                  <option key={s.id} value={s.id}>{s.nombre}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Fila 2: Responsable, Piscina */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                👤 Responsable <span className="text-red-500">*</span>
              </label>
              <select value={form.responsable_id}
                      onChange={e => set('responsable_id', e.target.value)}
                      className="w-full border rounded px-3 py-2">
                <option value="">Selecciona</option>
                {responsables.map(r => (
                  <option key={r.id} value={r.id}>{r.nombre} ({r.rol})</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                🔍 Nro. Piscina
              </label>
              <div className="relative">
                <input type="text" value={form.nro_piscina}
                       disabled={!form.sector_id}
                       onChange={e => {
                         set('nro_piscina', e.target.value);
                         buscarPiscina(e.target.value);
                       }}
                       onFocus={() => { if (form.sector_id) setMostrarSug(true); }}
                       onBlur={() => setTimeout(() => setMostrarSug(false), 200)}
                       placeholder={
                         form.sector_id
                           ? 'Escribe para buscar…'
                           : 'Selecciona primero un sector'
                       }
                       className={`w-full border rounded px-3 py-2 ${
                         !form.sector_id ? 'bg-gray-100 cursor-not-allowed' : ''
                       }`} />
                {mostrarSug && form.sector_id && piscinasSug.length > 0 && (
                  <div className="absolute z-30 w-full bg-white border rounded mt-1 shadow-lg max-h-48 overflow-y-auto">
                    {piscinasSug.map(p => (
                      <div key={p.id}
                           onMouseDown={(e) => e.preventDefault()}
                           onClick={() => {
                             set('nro_piscina', p.codigo);
                             setMostrarSug(false);
                           }}
                           className="px-3 py-2 hover:bg-sky-50 cursor-pointer text-sm">
                        <strong>{p.codigo}</strong>
                        <span className="text-gray-400 ml-2">({p.sector_nombre})</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Fila 3: Horas y tiempo calculado */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                🕐 Hora Reportada <span className="text-red-500">*</span>
              </label>
              <input type="time" value={form.hora_reportada}
                     onChange={e => set('hora_reportada', e.target.value)}
                     className="w-full border rounded px-3 py-2" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                ⚠️ Hora Alerta <span className="text-red-500">*</span>
              </label>
              <input type="time" value={form.hora_alerta}
                     onChange={e => set('hora_alerta', e.target.value)}
                     className="w-full border rounded px-3 py-2" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                ⏱️ Tiempo
              </label>
              <input type="text" readOnly
                     value={formatearTiempo(tiempoSeg)}
                     className="w-full border rounded px-3 py-2 bg-gray-100 text-gray-700" />
            </div>
          </div>

          {/* Fila 4: Tolva, Tecnología */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                🔢 Nro. Tolva
              </label>
              <input type="text" value={form.nro_tolva}
                     onChange={e => set('nro_tolva', e.target.value)}
                     placeholder="Ej: T-001"
                     className="w-full border rounded px-3 py-2" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                💻 Tecnología
              </label>
              <select value={form.tecnologia_id}
                      onChange={e => set('tecnologia_id', e.target.value)}
                      className="w-full border rounded px-3 py-2">
                <option value="">Selecciona</option>
                {tecnologias.map(t => (
                  <option key={t.id} value={t.id}>{t.nombre}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Fila 5: Tipo Alerta y Diagnóstico */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                🚨 Tipo Alerta <span className="text-red-500">*</span>
              </label>
              <ComboBox
                value={form.tipo_alerta_id}
                onChange={(v) => {
                  set('tipo_alerta_id', v);
                  set('tipo_diagnostico_id', '');
                }}
                options={tipos}
                placeholder="Escribe para buscar…"
                getLabel={(o) => o.nombre}
                getValue={(o) => o.id}
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                🩺 Diagnóstico
              </label>
              <ComboBox
                value={form.tipo_diagnostico_id}
                onChange={(v) => set('tipo_diagnostico_id', v)}
                options={diagFiltrados}
                placeholder="Escribe para buscar…"
                getLabel={(o) => o.nombre}
                getValue={(o) => o.id}
              />
            </div>
          </div>

          {/* Observaciones */}
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">
              📝 Observaciones
            </label>
            <textarea value={form.observaciones}
                      onChange={e => set('observaciones', e.target.value)}
                      rows="3"
                      className="w-full border rounded px-3 py-2"
                      placeholder="Observaciones opcionales…" />
          </div>

          {/* Estado */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                📌 Estado
              </label>
              <select value={form.estado}
                      onChange={e => set('estado', e.target.value)}
                      className="w-full border rounded px-3 py-2">
                <option value="en_proceso">En proceso</option>
                <option value="resuelta">Resuelta</option>
              </select>
            </div>
          </div>
        </div>

        <div className="px-5 py-3 border-t flex justify-end gap-2 bg-gray-50 rounded-b-xl">
          <button onClick={onCancel}
                  className="px-4 py-2 rounded border bg-white hover:bg-gray-100">
            Cancelar
          </button>
          <button onClick={guardar}
                  className="px-4 py-2 rounded bg-sky-600 text-white hover:bg-sky-700">
            Guardar
          </button>
        </div>
      </div>
    </div>
  );
}